import * as v from 'valibot'

import { NovelRunView } from './turn.js'

const Id = v.pipe(v.string(), v.minLength(1), v.maxLength(120))
const Count = v.pipe(v.number(), v.integer(), v.minValue(0))
const Json = v.record(v.string(), v.unknown())
const Side = v.picklist(['a', 'b'])
// 写作链上的一个模型：`provider#model`（与模型注册表的键同形）。
const ModelId = v.pipe(v.string(), v.minLength(1), v.maxLength(200))
// 两边各一个：[A, B]。
const ModelPair = v.pipe(v.array(ModelId), v.length(2))
const InputMode = v.picklist(['mixed', 'speech', 'action', 'continue'])

/**
 * 建对比（C27）：两边的写作模型，或导演开/关对比（两边同一个写作模型）；不给模型时 A、B 取写作链上前两个已配好的模型
 * （只有一个时两边同一个）。
 */
export const NovelComparisonCreateBody = v.strictObject({
  performer_ids: v.optional(ModelPair),
  director_modes: v.optional(v.pipe(v.array(v.boolean()), v.length(2))),
})

/**
 * 对比回合（不计费）：两边各跑一回合，side 只跑指定的一边（失败后重试）；performer_ids 给了就先换两边的写作模型再生成
 * （换模型再比一回合，各自从自己的进度续写）。输入上限与游玩回合一致（1000 字），continue 时 text 为空串。
 */
export const NovelComparisonTurnBody = v.strictObject({
  mode: InputMode,
  text: v.pipe(v.string(), v.maxLength(1000)),
  performer_ids: v.optional(ModelPair),
  side: v.optional(Side),
})

/** 采纳一边：只允许源对局属于发起对比的运营账号本人，否则 403 adopt_forbidden（勘误 17）。 */
export const NovelComparisonAdoptBody = v.strictObject({ side: Side })

/** 候选写作模型：写作链上的模型；configured 为 false 的不能选。 */
export const NovelComparisonModel = v.object({ id: ModelId, label: v.string(), configured: v.boolean() })

/** 最近一回合的指标：耗时（毫秒）、模型调用次数、输出字数；没生成过或还在生成时为 null。 */
export const NovelComparisonMetrics = v.object({
  elapsed_ms: v.nullable(Count),
  first_token_ms: v.optional(v.nullable(Count), null),
  writing_ms: v.optional(v.nullable(Count), null),
  calls: v.nullable(Count),
  output_chars: v.nullable(Count),
})

/**
 * 一边：run_id 是这一边的对比对局（观测页按它进入），model_id 是指定的写作模型，model 是最近一回合实际用到的模型。
 * status 是最近一回合的状态（ready 为还没生成过）；output 是最近一回合新生成的段（失败时是失败前的草稿），生成中的流式文本在 view.preview。
 * added_turns 是这一边在基线之后新增的回合数，采纳时追加的就是它们。
 */
export const NovelComparisonSide = v.object({
  side: Side,
  run_id: Id,
  model_id: v.nullable(ModelId),
  model_label: v.nullable(v.string()),
  director_enabled: v.boolean(),
  status: v.picklist(['ready', 'generating', 'completed', 'failed']),
  error_code: v.nullable(v.string()),
  input: v.nullable(v.object({ mode: InputMode, text: v.string() })),
  started_at: v.nullable(v.string()),
  model: v.nullable(v.string()),
  metrics: NovelComparisonMetrics,
  output: v.array(Json),
  added_turns: Count,
  adopted: v.boolean(),
  view: NovelRunView,
})

/**
 * 一次模型对比（C27）：comparison_id 即 A 边对局的 run_id。source 是源对局（已被删除时为 null），adoptable 表示源对局属于当前账号；
 * baseline 是复制时源对局活动分支的回合数与可见段数，history_tail 是基线最后 6 段；对比对局到 expires_at 自动清理。
 */
export const NovelComparison = v.object({
  comparison_id: Id,
  pack_title: v.string(),
  created_at: v.string(),
  expires_at: v.string(),
  director_comparison: v.boolean(),
  source: v.nullable(v.object({
    run_id: Id,
    user_id: v.string(),
    user_email: v.nullable(v.string()),
    status: v.picklist(['active', 'archived']),
    turn_count: Count,
    adoptable: v.boolean(),
  })),
  baseline: v.object({ through_seq: Count, history_count: Count, history_tail: v.array(Json) }),
  models: v.array(NovelComparisonModel),
  adopted_side: v.nullable(Side),
  sides: v.pipe(v.array(NovelComparisonSide), v.length(2)),
})
